import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOption, MatSelectModule } from '@angular/material/select';
import { ThemeSelectorService } from '@app/core/services/theme-service.service';
import { Hub } from '@app/pages/hub-management/models/hub';
import { HubType } from '@app/pages/hub-management/models/hub-type';
import { ShipmentManagerStateService } from '@app/pages/shipment-manager/services/shipment-manager-state.service';
import { LocalStorageEnum } from '@app/shared/models/enums/localStorageEnum';
import { TranslatePipe } from '@ngx-translate/core';
import { distinctUntilChanged, map, Observable, startWith } from 'rxjs';

@Component({
  selector: 'app-warehouse-selector',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatButtonToggleModule,
    TranslatePipe,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatIcon
  ],
  templateUrl: './warehouse-selector.component.html',
  styleUrls: ['./warehouse-selector.component.scss']
})
export class WarehouseSelectorComponent implements OnInit {
  @Output() warehouseChanged = new EventEmitter<{ warehouse: Hub }>();
  @Input() warehouses: Hub[];
  @Input()
  get initialValue(): { warehouse: Hub | null } | null {
    return this._initialValue;
  }

  set initialValue(value: { warehouse: Hub | null } | null) {
    if (value && value.warehouse) {
      this._initialValue = value;

      this.form.setValue(this.initialValue, { emitEvent: false });
      if (this.initialValue.warehouse) {
        this.setWarehouseType(this.initialValue.warehouse);
      }
    }
  }

  @ViewChild('warehouseInput') warehouseInput: ElementRef<HTMLInputElement>;
  @ViewChild('autoWarehouse') autoWarehouse: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;

  wareHouses$: Observable<Hub[]>;
  selectedWarehouseId: number;
  wareHouseTypeLabel: string = '';

  form = this.fb.group({
    warehouse: null as Hub | null
  });

  private _initialValue: { warehouse: Hub | null } | null = null; // eslint-disable-line @typescript-eslint/naming-convention

  constructor(private fb: FormBuilder,
    public themeService: ThemeSelectorService,
    private readonly shipmentManagerStateService: ShipmentManagerStateService) {
  }

  get themeColor(): string {
    return this.themeService.companyThemeBo?.bckColor ?? '#3f51b5';
  }

  ngOnInit(): void {
    this.wareHouses$ = this.form.get('warehouse')?.valueChanges.pipe(
      startWith(''),
      map((value: string | Hub) => {
        const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
        return filterValue
          ? this.warehouses.filter(warehouse =>
            warehouse.friendlyName.toLowerCase().includes(filterValue)
          )
          : this.warehouses;
      })
    ) as Observable<Hub[]>;

    this.form.valueChanges.pipe(
      distinctUntilChanged((a, b) => this.deepEqual(a, b))
    ).subscribe(value => {
      if (value.warehouse && typeof value.warehouse === 'object') {
        this.warehouseChanged.emit({ warehouse: value.warehouse as Hub });
        this.setWarehouseType(value.warehouse);
      } else {
        this.wareHouseTypeLabel = '';
      }
    });
  }

  onWarehouseClick(): void {
    const value = this.form.get('warehouse')?.value;
    if (typeof this.form.controls.warehouse.value !== 'string') {
      this.form.controls.warehouse.setValue(value, { emitEvent: true });
    }
    this.autocompleteTrigger.openPanel();
  }

  warehouseDisplayFn(warehouse: Hub): string {
    return warehouse ? `${warehouse.friendlyName}` : '';
  }

  private setWarehouseType(warehouse: Hub) {
    if (warehouse.hubType === HubType.Vendor) {
      this.wareHouseTypeLabel = 'SHIPMENT_MANAGEMENT.VENDOR_WAREHOUSE';
      this.shipmentManagerStateService.updateCurrentWarehouseVendorFlag(true);
      localStorage.setItem(LocalStorageEnum.IsVendorWarehouse, 'true');
    } else if (warehouse.hubType === HubType.LastMile) {
      this.wareHouseTypeLabel = 'SHIPMENT_MANAGEMENT.LAST_MILE_LOCATION';
      this.shipmentManagerStateService.updateCurrentWarehouseVendorFlag(false);
      localStorage.setItem(LocalStorageEnum.IsVendorWarehouse, 'false');
    }
  }

  private deepEqual(a: any, b: any): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }
}
